import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Link,
  Flex,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box minH="100vh" bg="white" color="purple.900">
      {/* Navigation */}
      <Box as="nav" py={6} px={8} position="fixed" top={0} left={0} right={0} zIndex={1000} bg="white" backdropFilter="blur(10px)" borderBottom="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center">
            <RouterLink to="/">
              <Link>
                <Box>
                  <Heading size="xl" color="purple.500" fontWeight="bold">
                    IDK
                  </Heading>
                  <Text fontSize="sm" color="purple.600" mt={-1}>
                    I Don't Know
                  </Text>
                </Box>
              </Link>
            </RouterLink>
            <HStack gap={8}>
              <RouterLink to="/">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontWeight="medium">
                  Home
                </Link>
              </RouterLink>
              <RouterLink to="/support">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontWeight="medium">
                  Support
                </Link>
              </RouterLink>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box pt={32} pb={20}>
        <Container maxW="4xl">
          <VStack gap={12} align="start">
            {/* Header */}
            <VStack gap={6} align="start" w="full">
              <Heading size="3xl" fontWeight="bold" color="purple.900">
                Privacy Policy
              </Heading>
              <Text fontSize="lg" color="purple.700">
                Last updated: January 2025
              </Text>
              <Box w="full" h="1px" bg="purple.200" />
            </VStack>

            {/* Introduction */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Introduction
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                Welcome to IDK ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services.
              </Text>
            </VStack>

            {/* Information We Collect */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Information We Collect
              </Heading>
              
              <VStack gap={4} align="start" w="full">
                <Box>
                  <Heading size="lg" color="purple.700" mb={3}>
                    Personal Information
                  </Heading>
                  <VStack gap={2} align="start">
                    <Text fontSize="md" color="purple.700">• Email address (for account creation and communication)</Text>
                    <Text fontSize="md" color="purple.700">• Username and profile information</Text>
                    <Text fontSize="md" color="purple.700">• Location data (with your permission, for activity suggestions)</Text>
                    <Text fontSize="md" color="purple.700">• Payment information (processed securely through third-party providers)</Text>
                  </VStack>
                </Box>

                <Box>
                  <Heading size="lg" color="purple.700" mb={3}>
                    Usage Information
                  </Heading>
                  <VStack gap={2} align="start">
                    <Text fontSize="md" color="purple.700">• App usage patterns and preferences</Text>
                    <Text fontSize="md" color="purple.700">• Activity selections and feedback</Text>
                    <Text fontSize="md" color="purple.700">• Device information and operating system</Text>
                    <Text fontSize="md" color="purple.700">• IP address and device identifiers</Text>
                  </VStack>
                </Box>
              </VStack>
            </VStack>

            {/* How We Use Information */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                How We Use Your Information
              </Heading>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• Provide personalized activity recommendations based on your location and preferences</Text>
                <Text fontSize="md" color="purple.700">• Improve our services and develop new features</Text>
                <Text fontSize="md" color="purple.700">• Communicate with you about updates, features, and support</Text>
                <Text fontSize="md" color="purple.700">• Process payments and manage subscriptions</Text>
                <Text fontSize="md" color="purple.700">• Ensure app security and prevent fraud</Text>
                <Text fontSize="md" color="purple.700">• Comply with legal obligations</Text>
              </VStack>
            </VStack>

            {/* Data Sharing */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Information Sharing and Disclosure
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </Text>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• With your explicit consent</Text>
                <Text fontSize="md" color="purple.700">• With service providers who assist in app operations (under strict confidentiality agreements)</Text>
                <Text fontSize="md" color="purple.700">• When required by law or to protect our rights</Text>
                <Text fontSize="md" color="purple.700">• In connection with a business transfer or merger</Text>
              </VStack>
            </VStack>

            {/* Data Security */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Data Security
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and regular security audits. However, no method of transmission over the internet is 100% secure.
              </Text>
            </VStack>

            {/* Your Rights */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Your Rights and Choices
              </Heading>
              <VStack gap={2} align="start" w="full">
                <Text fontSize="md" color="purple.700">• Access and update your personal information</Text>
                <Text fontSize="md" color="purple.700">• Delete your account and associated data</Text>
                <Text fontSize="md" color="purple.700">• Opt out of marketing communications</Text>
                <Text fontSize="md" color="purple.700">• Control location sharing permissions</Text>
                <Text fontSize="md" color="purple.700">• Request a copy of your data</Text>
              </VStack>
            </VStack>

            {/* Cookies and Tracking */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Cookies and Tracking Technologies
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                Our app may use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and provide personalized content. You can control these settings through your device preferences.
              </Text>
            </VStack>

            {/* Children's Privacy */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Children's Privacy
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.
              </Text>
            </VStack>

            {/* International Users */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                International Users
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located.
              </Text>
            </VStack>

            {/* Changes to Policy */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Changes to This Privacy Policy
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the service after any modifications constitutes acceptance of the updated Privacy Policy.
              </Text>
            </VStack>

            {/* Contact Information */}
            <VStack gap={6} align="start" w="full">
              <Heading size="xl" color="purple.800">
                Contact Us
              </Heading>
              <Text fontSize="md" color="purple.700" lineHeight="tall">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </Text>
              <VStack gap={2} align="start">
                <Text fontSize="md" color="purple.700">Email: privacy@idkapp.com</Text>
                <Text fontSize="md" color="purple.700">Support: support@idkapp.com</Text>
                <Text fontSize="md" color="purple.700">Website: <Link href="/support" color="purple.500">www.idkapp.com/support</Link></Text>
              </VStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box py={12} bg="purple.50" borderTop="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Text color="purple.600" fontSize="sm">
              © 2025 IDK App. All rights reserved.
            </Text>
            <HStack gap={6}>
              <RouterLink to="/">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Home
                </Link>
              </RouterLink>
              <RouterLink to="/support">
                <Link color="purple.600" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Support
                </Link>
              </RouterLink>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicyPage;
